import { normalizeStoryUserId } from '../api/socialStoriesApi';

export type MessagingPeerCacheEntry = {
  label: string;
  avatarSrc: string;
  updatedAt: number;
};

const STORAGE_KEY = 'ofrontend.messagingPeerCache.v1';

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function readAll(): Record<string, MessagingPeerCacheEntry> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) {
      return {};
    }
    const out: Record<string, MessagingPeerCacheEntry> = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (!isRecord(value)) {
        continue;
      }
      const label = typeof value.label === 'string' ? value.label.trim() : '';
      const avatarSrc = typeof value.avatarSrc === 'string' ? value.avatarSrc.trim() : '';
      const updatedAt =
        typeof value.updatedAt === 'number' && Number.isFinite(value.updatedAt)
          ? value.updatedAt
          : 0;
      if (!label) {
        continue;
      }
      out[key] = { label, avatarSrc, updatedAt };
    }
    return out;
  } catch {
    return {};
  }
}

function writeAll(map: Record<string, MessagingPeerCacheEntry>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* private mode / quota */
  }
}

export function rememberMessagingPeerDisplay(
  peerUserId: string,
  entry: { label: string; avatarSrc?: string | null },
): void {
  const id = normalizeStoryUserId(peerUserId);
  const label = entry.label.trim();
  if (!id || !label) {
    return;
  }
  const avatarSrc = entry.avatarSrc?.trim() || '';
  const map = readAll();
  const prev = map[id];
  if (
    prev &&
    prev.label === label &&
    (prev.avatarSrc === avatarSrc || (!avatarSrc && prev.avatarSrc))
  ) {
    return;
  }
  map[id] = {
    label,
    avatarSrc: avatarSrc || prev?.avatarSrc || '',
    updatedAt: Date.now(),
  };
  writeAll(map);
}

export function readMessagingPeerDisplay(peerUserId: string): MessagingPeerCacheEntry | null {
  const id = normalizeStoryUserId(peerUserId);
  if (!id) {
    return null;
  }
  return readAll()[id] ?? null;
}
